import React, { useState } from "react";
import kamlaLogo from '../assests/kamlaLogo.png';
import '../styles/main.css';

// --- Utility Functions ---

function numberToWords(num) {
  if (num == null) return "-";
  num = Number(num);
  if (isNaN(num)) return "-";

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven',
    'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const scales = ['', 'Thousand', 'Lakh', 'Crore'];

  function chunkToWords(n) {
    let word = "";
    if (n > 99) {
      word += ones[Math.floor(n / 100)] + " Hundred";
      n = n % 100;
      if (n) word += " and ";
    }
    if (n < 20) {
      word += ones[n];
    } else {
      word += tens[Math.floor(n / 10)];
      if (n % 10) word += " " + ones[n % 10];
    }
    return word;
  }

  function splitIndianNumber(n) {
    const str = n.toString();
    let chunks = [];
    let end = str.length;
    chunks.push(parseInt(str.substring(Math.max(0, end - 3), end), 10));
    end -= 3;
    while (end > 0) {
      let start = Math.max(0, end - 2);
      chunks.push(parseInt(str.substring(start, end), 10));
      end -= 2;
    }
    return chunks;
  }

  if (num === 0) return "Zero";
  const numberChunks = splitIndianNumber(parseInt(num));
  let wordArray = [];
  for (let i = 0; i < numberChunks.length; i++) {
    if (numberChunks[i] > 0) {
      wordArray.push(chunkToWords(numberChunks[i]) + (scales[i] ? " " + scales[i] : ""));
    }
  }
  return wordArray.reverse().join(" ").trim();
}

function formatDateToDDMMYYYY(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return "";
  const day = ("0" + date.getDate()).slice(-2);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function formatTime(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-IN", {
        hour: "2-digit", 
        minute: "2-digit",
        hour12: true
    });
  } catch(e) { return ""; }
}

const ServiceReceipt = ({ show, data, patient, companyDetails, loggedInUser, onClose, onPrint }) => {
  const [showHeader, setShowHeader] = useState(true);

  if (!show || !data || !companyDetails) return null;

  const handlePrint = () => {
    if (onPrint) onPrint();
    else window.print();
  };

  const handleToggleHeader = () => {
    setShowHeader(!showHeader);
  };

  // --- Data Mapping & Consolidation ---
  const rawServices = Array.isArray(data) ? data : [];
  const safePatient = patient || {};
  const pName = safePatient.name || safePatient.patientName || safePatient.PatientName || "";
  const pUHID = safePatient.uhidNo || safePatient.UHID_No || safePatient.uhiD_No || "-";
  const pMobile = safePatient.mobileNo || safePatient.MobileNo || "";
  const pAddress = safePatient.address || "";
  const pID = safePatient.ipD_SRNO || safePatient.opD_SRNO || safePatient.s_IPD_ID || "-";
  
  const firstRecord = rawServices[0] || {};
  const rawDate = firstRecord.servdate || firstRecord.Servdate || safePatient.regDate || new Date();
  const createdBy = firstRecord.createdBy || loggedInUser || "System";

  // ✅ CONSOLIDATE SERVICES
  const serviceMap = {};
  rawServices.forEach((s) => {
    const key = s.serviceName || s.name || "Unknown Service";
    if (!serviceMap[key]) {
      serviceMap[key] = {
        description: key,
        rate: Number(s.servRate || s.rate || 0),
        qty: 0,
        amount: 0
      };
    }
    const currentQty = Number(s.qty || s.QTY || s.unit || 1);
    serviceMap[key].qty += currentQty;
    serviceMap[key].amount += Number(s.amount || (serviceMap[key].rate * currentQty));
  });
  
  const consolidatedArray = Object.values(serviceMap).map((item, index) => ({ ...item, sr: index + 1 }));

  // ✅ PAGINATION LOGIC (20 Items Per Page)
  const ITEMS_PER_PAGE = 20; 
  const pages = [];
  let runningTotal = 0;

  for (let i = 0; i < consolidatedArray.length; i += ITEMS_PER_PAGE) {
    const pageItems = consolidatedArray.slice(i, i + ITEMS_PER_PAGE);
    const pageTotal = pageItems.reduce((sum, item) => sum + item.amount, 0);
    pages.push({
      items: pageItems,
      broughtForward: runningTotal, 
      pageTotal: pageTotal,         
      carriedForward: runningTotal + pageTotal 
    });
    runningTotal += pageTotal;
  }

  if (pages.length === 0) pages.push({ items: [], broughtForward: 0, pageTotal: 0, carriedForward: 0 });

  return (
    <>
      <style>{`
        :root {
          --marg-border: 1px solid #000;
          --marg-border-thick: 2px solid #000;
          --marg-font: Arial, Helvetica, sans-serif;
        }

        .marg-receipt-wrapper { font-family: var(--marg-font); color: #000; background: #fff; font-size: 12px; }

        /* ✅ Ensure padding and borders don't break percentage widths */
        .marg-receipt-wrapper * {
          box-sizing: border-box;
        }

        .marg-outer-box {
          border: var(--marg-border-thick);
          margin-bottom: 20px;
          height: 265mm; /* ✅ Updated height for A4 */
          box-sizing: border-box;
          position: relative;
          background: #fff;
          display: flex;
          flex-direction: column;
          page-break-inside: avoid;
        }

        .marg-header { border-bottom: var(--marg-border-thick); padding: 8px 10px; text-align: center; position: relative; display: flex; justify-content: space-between; align-items: center; }
        
        .marg-title-strip {
          background-color: #e0e0e0 !important; 
          border-bottom: var(--marg-border-thick);
          text-align: center;
          padding: 4px;
          font-weight: 900;
          font-size: 14px;
          letter-spacing: 1px;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .marg-patient-box { display: flex; border-bottom: var(--marg-border-thick); }
        .marg-patient-col { padding: 5px 10px; }
        .marg-patient-left { width: 60%; border-right: var(--marg-border-thick); }
        .marg-patient-right { width: 40%; }
        .marg-patient-col table { width: 100%; font-size: 12px; line-height: 1.4; }

        /* ✅ Flexbox Table Container to enforce strict widths */
        .marg-table-container { flex-grow: 1; display: flex; flex-direction: column; width: 100%; }

        .marg-table-header {
          display: flex;
          width: 100%;
          border-bottom: var(--marg-border);
          font-weight: bold;
          text-transform: uppercase;
          font-size: 11px;
          background-color: #e0e0e0 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .marg-table-row {
          display: flex;
          width: 100%;
          font-size: 12px;
        }

        /* ✅ Trick to make lines go all the way down */
        .marg-table-filler {
          flex-grow: 1;
          display: flex;
          width: 100%;
          align-items: stretch;
        }

        /* ✅ ALIGNMENT 60-40 FIX */
        .marg-col-sr   { width: 6%;  text-align: center; padding: 4px 5px; border-right: var(--marg-border); }
        .marg-col-desc { width: 54%; text-align: left;   padding: 4px 5px; border-right: var(--marg-border); }
        .marg-col-rate { width: 12%; text-align: right;  padding: 4px 5px; border-right: var(--marg-border); }
        .marg-col-qty  { width: 10%; text-align: center; padding: 4px 5px; border-right: var(--marg-border); }
        .marg-col-amt  { width: 18%; text-align: right;  padding: 4px 5px; }

        .marg-page-total {
          display: flex;
          width: 100%;
          border-top: var(--marg-border);
          border-bottom: var(--marg-border-thick);
          font-weight: bold;
          font-size: 12px;
          background-color: #f9f9f9 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* ✅ FOOTER GRID */
        .marg-footer-grid { display: flex; border-top: var(--marg-border-thick); border-bottom: var(--marg-border-thick); margin-top: auto; }
        .marg-words-box { width: 60%; border-right: var(--marg-border-thick); padding: 10px; display: flex; flex-direction: column; justify-content: space-between; }
        .marg-calc-box { width: 40%; }
        
        .marg-calc-table { width: 100%; height: 100%; border-collapse: collapse; }
        .marg-calc-table td { padding: 10px; font-size: 14px; }

        .marg-highlight-row td {
          background-color: #e0e0e0 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .marg-signature-box { display: flex; justify-content: space-between; padding: 40px 15px 10px 15px; background: #fff; }
        .marg-sig-line { border-top: 1px solid #000; padding-top: 5px; text-align: center; font-weight: bold; font-size: 11px; width: 100%; }

        @media print {
          @page { size: A4 portrait; margin: 5mm; }
          html, body { height: auto !important; overflow: visible !important; background: white !important; margin: 0 !important; padding: 0 !important; }
          body * { visibility: hidden; }
          #marg-print-section, #marg-print-section * { visibility: visible; }
          #marg-print-section { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
          .page-break { page-break-after: always; }
        }
      `}</style>

      <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}>
        <div className="modal-dialog modal-lg" style={{ maxWidth: '950px', margin: '20px auto' }}>
          <div className="modal-content border-0">
            <div id="marg-print-section" className="marg-receipt-wrapper p-2 bg-white">
              {pages.map((pageData, pageIndex) => (
                <div key={pageIndex} className={`marg-outer-box ${pageIndex < pages.length - 1 ? 'page-break' : ''}`}>
                  
                  {/* Header */}
                  {showHeader ? (
                    <div className="marg-header">
                      <img src={kamlaLogo} alt="Logo" style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                      <div style={{ flexGrow: 1 }}>
                        <h2 style={{ margin: '0', fontWeight: '900', fontSize: '22px', textTransform: 'uppercase' }}>{companyDetails.company_Name}</h2>
                        <p style={{ margin: '0' }}>{companyDetails.address}</p>
                        <p style={{ margin: '0' }}>Ph: {companyDetails.contact1} | Email: {companyDetails.email_ID}</p>
                      </div>
                      <div style={{ textAlign: 'right', fontSize: '11px' }}>
                        <strong>Reg No:</strong> {companyDetails.reg_No || 'N/A'}<br/>
                        {pages.length > 1 && <strong>Page {pageIndex + 1}/{pages.length}</strong>}
                      </div>
                    </div>
                  ) : <div style={{ height: "100px", position: "relative" }}>
                        {pages.length > 1 && (
                            <div style={{ position: 'absolute', right: '15px', top: '10px', fontSize: '11px', fontWeight: 'bold' }}>
                                Page {pageIndex + 1} of {pages.length}
                            </div>
                        )}
                      </div>
                  }

                  <div className="marg-title-strip">SERVICE RECEIPT</div>

                  {/* Patient Info */}
                  <div className="marg-patient-box">
                    <div className="marg-patient-col marg-patient-left">
                      <table>
                        <tbody>
                          <tr><td width="25%"><strong>Patient</strong></td><td width="5%">:</td><td><strong style={{ fontSize: '14px', textTransform: 'uppercase' }}>{pName}</strong></td></tr>
                          <tr><td><strong>UHID No.</strong></td><td>:</td><td>{pUHID}</td></tr>
                          <tr><td><strong>Address</strong></td><td>:</td><td>{pAddress || "-"}</td></tr>
                        </tbody>
                      </table>
                    </div>
         {/* Right Aligned Data */}
                  <div className="marg-patient-col marg-patient-right" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <table style={{ width: '50%', whiteSpace: 'nowrap' }}>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: 'left' }}><strong>IPD Id.</strong></td>
                          <td style={{ textAlign: 'center', padding: '0 4px' }}>:</td>
                          <td style={{ textAlign: 'left' }}><strong>{pID}</strong></td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: 'left' }}><strong>Date</strong></td>
                          <td style={{ textAlign: 'center', padding: '0 4px' }}>:</td>
                          <td style={{ textAlign: 'left' }}>{formatDateToDDMMYYYY(rawDate)}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: 'left' }}><strong>Time</strong></td>
                          <td style={{ textAlign: 'center', padding: '0 4px' }}>:</td>
                          <td style={{ textAlign: 'left' }}>{formatTime(rawDate)}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: 'left' }}><strong>Mobile</strong></td>
                          <td style={{ textAlign: 'center', padding: '0 4px' }}>:</td>
                          <td style={{ textAlign: 'left' }}>{pMobile || "-"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  </div>

                  {/* Items Table */}
                  <div className="marg-table-container">
                    
                    <div className="marg-table-header">
                      <div className="marg-col-sr">S.No.</div>
                      <div className="marg-col-desc">Particulars / Service Description</div>
                      <div className="marg-col-rate">Rate (Rs)</div>
                      <div className="marg-col-qty">Qty</div>
                      <div className="marg-col-amt">Amount (Rs)</div>
                    </div>

                    {/* ✅ Brought Forward (If not page 1) */}
                    {pageIndex > 0 && (
                      <div className="marg-page-total" style={{ borderTop: 'none', borderBottom: 'var(--marg-border)' }}>
                        <div className="marg-col-sr"></div>
                        <div className="marg-col-desc" style={{ textAlign: 'right' }}>Brought Forward (B/F):</div>
                        <div className="marg-col-rate"></div>
                        <div className="marg-col-qty"></div>
                        <div className="marg-col-amt">{pageData.broughtForward.toFixed(2)}</div>
                      </div>
                    )}

                    {/* Items List */}
                    {pageData.items.map((item) => (
                      <div className="marg-table-row" key={item.sr}>
                        <div className="marg-col-sr">{item.sr}</div>
                        <div className="marg-col-desc">{item.description}</div>
                        <div className="marg-col-rate">{item.rate.toFixed(2)}</div>
                        <div className="marg-col-qty">{item.qty}</div>
                        <div className="marg-col-amt">{item.amount.toFixed(2)}</div>
                      </div>
                    ))}

                    {/* ✅ Empty filler to stretch vertical lines to the bottom safely */}
                    <div className="marg-table-filler">
                      <div className="marg-col-sr"></div>
                      <div className="marg-col-desc"></div>
                      <div className="marg-col-rate"></div>
                      <div className="marg-col-qty"></div>
                      <div className="marg-col-amt"></div>
                    </div>

                  </div>

                  {/* Page Total / Carried Forward */}
                  <div className="marg-page-total" style={{ borderTop: 'none' }}>
                    <div className="marg-col-sr"></div>
                    <div className="marg-col-desc" style={{ textAlign: 'right' }}>
                      {pageIndex < pages.length - 1 ? "Carried Forward (C/F) Total:" : "Total of Items:"}
                    </div>
                    <div className="marg-col-rate"></div>
                    <div className="marg-col-qty"></div>
                    <div className="marg-col-amt">{pageData.carriedForward.toFixed(2)}</div>
                  </div>

                  {/* Footer (ONLY ON LAST PAGE) */}
                  {pageIndex === pages.length - 1 ? (
                    <>
                      <div className="marg-footer-grid">
                        <div className="marg-words-box">
                          <div>
                            <p style={{ margin: "0" }}><strong>Total Amount in Words:</strong></p>
                            <p style={{ margin: "0", fontSize: '13px', fontWeight: 'bold' }}>Rupees {numberToWords(runningTotal)} Only</p>
                          </div>
                          <div style={{ fontSize: '10px', marginTop: '10px' }}>
                            <strong>Disclaimer:</strong> All disputes are subject to local jurisdiction.
                          </div>
                        </div>
                        <div className="marg-calc-box">
                          <table className="marg-calc-table">
                            <tbody>
                              <tr className="marg-highlight-row">
                                <td style={{ borderRight: '1px solid #000' }}><strong>NET AMOUNT</strong></td>
                                <td style={{ textAlign: "right", fontWeight: "bold", fontSize: "16px" }}>₹ {runningTotal.toFixed(2)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="marg-signature-box">
                        <div style={{ textAlign: "center", width: "30%", display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                            <div className="marg-sig-line">Patient Signature</div>
                        </div>
                        <div style={{ textAlign: "center", width: "30%", display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                            <div style={{ fontSize: '11px', marginBottom: '2px' }}>{createdBy}</div>
                            <div className="marg-sig-line">Generated By</div>
                        </div>
                        <div style={{ textAlign: "center", width: "30%", display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                            <div className="marg-sig-line">Hospital Authority</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{ padding: "10px", textAlign: "right", fontWeight: "bold", fontSize: "12px", fontStyle: "italic", borderTop: "var(--marg-border-thick)" }}>
                      Continued to next page...
                    </div>
                  )}

                </div>
              ))}
            </div>

            {/* Buttons */}
            <div className="no-print d-flex justify-content-end gap-2 p-3 bg-light border-top">
              <button className={`btn btn-sm ${showHeader ? "btn-outline-secondary" : "btn-warning"} px-3`} onClick={handleToggleHeader}>
                <i className={`bi ${showHeader ? "bi-eye-slash" : "bi-eye"} me-2`}></i>
                {showHeader ? "Use Letterhead (Hide Header)" : "Show Header"}
              </button>
              <button className="btn btn-secondary btn-sm px-4" onClick={onClose}>Close</button>
              <button className="btn btn-primary btn-sm fw-bold px-4" onClick={handlePrint} style={{ background: 'var(--theme-color, #0088cc)', borderColor: 'var(--theme-color, #0088cc)' }}>
                  <i className="bi bi-printer me-2"></i> Print Receipt
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceReceipt;